import type { RouteLocationNormalizedLoaded } from "#vue-router";
import type {
  ApiCategory,
  ApiResult,
  ApiTag,
  CurrentPage,
  SearchTermInterface,
  SearchTermTypes,
} from "~/stores/types/searchTermInterface";
import { defineStore, useRuntimeConfig } from '#imports';
import { useUserStore } from '~/stores/user';

export const useSearchStore = defineStore('search', {
  state: () => ({
    filters: {
      categories: [] as string[],
      tags: [] as string[],
      freeText: [] as string[],
      sale: false,
    },
    urlFilters: {
      vendor: null as string|null,
      categories: [] as string[],
      tags: [] as string[],
    },
    data: {
      categories: [] as ApiCategory[],
      categoryTree: [] as ApiCategory[],
      tags: [] as ApiTag[],
      results: [] as ApiResult[],
      searchError: null,
      currentPage: null as CurrentPage|null,
    },
    meta: {
      currentPage: 1,
      totalResults: 0,
      userOrderBy: null,
      searchStatus: 0,
      isOnDetailPage: false,
    },
    currentAbortControllers: [],
  }),
  getters: {
    defaultOrderBy (): string {
      if (this.filters.freeText.length > 0) {
        return 'relevance';
      }

      const otherRelevant = [
        ...this.filters.categories,
        ...this.filters.tags,
        ...this.urlFilters.categories,
        ...this.urlFilters.tags,
      ];

      return otherRelevant.length > 0
        ? 'popularity'
        : 'date';
    },
    flatSearchResults (state): SearchTermInterface[] {
      const response: SearchTermInterface[] = [];

      state.urlFilters.categories.forEach((categorySlug: string, index: number) => {
        response.push({
          index: index,
          type: 'urlCategories',
          slug: categorySlug,
          name: state.data.categories.find((category) => category.slug === categorySlug)?.name,
        });
      });

      state.urlFilters.tags.forEach((tagSlug: string, index: number) => {
        response.push({
          index: index,
          type: 'urlTags',
          slug: tagSlug,
          name: state.data.tags.find((tag) => tag.slug === tagSlug)?.name,
        });
      });

      state.filters.categories.forEach((categorySlug: string, index: number) => {
        response.push({
          index: index,
          type: 'categories',
          slug: categorySlug,
          name: state.data.categories.find((category) => category.slug === categorySlug)?.name,
        });
      });

      state.filters.tags.forEach((tagSlug: string, index: number) => {
        response.push({
          index: index,
          type: 'tags',
          slug: tagSlug,
          name: state.data.tags.find((tag) => tag.slug === tagSlug)?.name,
        });
      });

      state.filters.freeText.forEach((tagSlug: string, index: number) => {
        response.push({
          index: index,
          type: 'freeText',
          slug: encodeURI(tagSlug),
          name: tagSlug,
        });
      });

      if (state.filters.sale) {
        response.push({
          index: 0,
          type: 'sale',
          slug: 'sale',
          name: 'On Sale',
        });
      }

      if (state.urlFilters.vendor) {
        response.push({
          index: 0,
          type: 'vendor',
          slug: state.urlFilters.vendor,
          name: state.data.currentPage?.name,
        });
      }

      return response;
    },
  },
  actions: {
    getSelectedParamsAsObject (): Record<string, string|boolean> {
      const response = {};

      if (this.filters.freeText.length) {
        response.query = this.filters.freeText.join(',');
      }

      if (this.filters.categories.length) {
        response.categories = this.filters.categories.join(',');
      }

      if (this.filters.tags.length) {
        response.tags = this.filters.tags.join(',');
      }

      if (this.filters.sale) {
        response['on-sale'] = true;
      }

      if (this.meta.userOrderBy) {
        response.orderby = this.meta.userOrderBy;
      }

      if (this.meta.currentPage > 1) {
        response.page = this.meta.currentPage;
      }

      return response;
    },
    getUrlParamsAsObject (): Record<string, string|boolean> {
      const baseParams = this.getSelectedParamsAsObject();

      if (this.urlFilters.categories.length > 0) {
        const slug = this.urlFilters.categories.join(',');

        if (baseParams.categories) {
          baseParams.categories += `,${slug}`;
        } else {
          baseParams.categories = slug;
        }
      } else if (this.urlFilters.vendor) {
        baseParams.vendor = this.urlFilters.vendor;
      } else if (this.urlFilters.tags.length > 0) {
        const tagSlug = this.urlFilters.tags.join(',');

        if (baseParams.tags) {
          baseParams.tags += tagSlug;
        } else {
          baseParams.tags = tagSlug;
        }
      }

      return baseParams;
    },
    getParamsAsUrl (): string|null {
      const queryParams = [];
      const urlParams = this.getUrlParamsAsObject();

      if (!urlParams.orderby) {
        urlParams.orderby = this.defaultOrderBy;
      }

      Object.keys(urlParams).forEach((paramKey) => {
        queryParams.push(`${paramKey}=${urlParams[paramKey]}`);
      });

      return queryParams.join('&');
    },
    async setInitialState(route: RouteLocationNormalizedLoaded): void {
      // Reset data that needs to be refreshed on page change (ie: when navigating through breadcrumbs)
      this.removeAllFilters();
      // Parse query to set filters accordingly
      const params = route.query;
      const { pageParams } = route.params;
      let uriSearchString = '';

      if (pageParams) {
        // The first param differentiates between software, sfx, vendors and categories
        const pageType = pageParams[0];

        if ('category' === pageType) {
          // Take the last category because if we're on a subcategory page then we don't care about the parent category here.
          uriSearchString = `&category=${pageParams.findLast((categoryName) => !!categoryName)}`;

          for (let i = 1; i < pageParams.length; i++) {
            // If the url ends in a trailing slash (/) then that is considered an empty string in pageParams list
            if (pageParams[i]) {
              this.urlFilters.categories.push(pageParams[i]);
            }
          }
        } else if ('sound-designer' === pageType) {
          this.urlFilters.vendor = pageParams[1];
          uriSearchString = `&vendor=${pageParams[1]}`;
        } else if ('tag' === pageType) {
          uriSearchString = `&tag=${pageParams[1]}`;
          this.urlFilters.tags = [pageParams[1]];
        }
      }

      if (params.categories) {
        this.filters.categories = params.categories.split(',');
      }

      if (params.tags) {
        this.filters.tags = params.tags.split(',');
      }

      if (params['on-sale']) {
        this.filters.sale = true;
      }

      if (params.freeText || params.query) {
        this.filters.freeText = params.freeText
          ? params.freeText.split(',')
          : params.query.split(',');
      }

      const pageParam = parseInt(params.page, 10);
      if (pageParam >= 1) {
        this.meta.currentPage = pageParam;
      } else {
        this.meta.currentPage = 1;
      }

      if (params.orderby) {
        this.meta.userOrderBy = params.orderby;
      }

      // we are on a detail page if we want to display seo relevant content
      this.meta.isOnDetailPage = uriSearchString !== '';
      const config = useRuntimeConfig();
      const response = await fetch(`${config.public.baseUrl}/wp-json/content-search/v1/search?type=product-category${uriSearchString}`)
      const data = await response.json();
      this.data.categoryTree = data['category-tree'];
      this.data.tags = data['tag-tree'];
      this.data.currentPage = data['current-page'];
    },
    toggleFilter (filterType: SearchTermTypes, filterValue: string|boolean): void {
      if (['categories', 'tags', 'freeText'].includes(filterType)) {
        const index = this.filters[filterType].indexOf(filterValue)
        if (index >= 0) {
          this.filters[filterType].splice(index, 1);
        } else {
          this.filters[filterType].push(filterValue);
        }
      } else if (filterType === 'vendor') {
        this.urlFilters.vendor = null;
      } else if (filterType === 'urlCategories') {
        const index = this.urlFilters.categories.indexOf(filterValue)
        if (index >= 0) {
          this.urlFilters.categories.splice(index, 1);
        } else {
          this.urlFilters.categories.push(filterValue);
        }
      } else if ('urlTags' === filterType) {
        this.urlFilters.tags = [];
      } else {
        this.filters[filterType] = filterValue;
      }

      this.meta.currentPage = 1;
    },
    removeFilter (filterItem: SearchTermInterface): void {
      if (['categories', 'tags', 'freeText'].includes(filterItem.type)) {
        this.filters[filterItem.type].splice(filterItem.index, 1);
      } else if ('sale' === filterItem.type) {
        this.filters.sale = false;
      } else if ('vendor' === filterItem.type) {
        this.urlFilters.vendor = null;
      } else if ('urlCategories' === filterItem.type) {
        this.urlFilters.categories.splice(filterItem.index);
      } else if ('urlTags' === filterItem.type) {
        this.urlFilters.tags = [];
      }
      this.meta.currentPage = 1;
    },
    removeAllFilters (): void {
      this.data.currentPage = null;
      this.urlFilters.vendor = null;
      this.urlFilters.categories = [];
      this.urlFilters.tags = [];
      this.filters.categories = [];
      this.filters.tags = [];
      this.filters.sale = false;
      this.filters.freeText = [];
      this.meta.userOrderBy = null;
    },
    toggleSale (newValue: boolean): void {
      this.filters.sale = newValue;
      this.meta.currentPage = 1;
    },
    addFreeText (queryString: string): void {
      let trimmedQueryString = queryString.trim();

      // check if query string is already in the array
      if (this.filters.freeText.includes( trimmedQueryString )) {
        return;
      }
      // check if queryString is empty or does only contain whitespace
      if (!trimmedQueryString) {
        return;
      }
      this.filters.freeText.push(trimmedQueryString);
      this.meta.currentPage = 1;
    },
    async fetchData (): Promise<void> {
      this.meta.searchStatus = 1;

      const paramsFromUrl = this.getParamsAsUrl();
      const queryParams: string[] = [
        'type=product',
      ];

      if (paramsFromUrl) {
        queryParams.push(paramsFromUrl);
      }

      // Add country code for correct sale badge calculation
      const userStore = useUserStore();
      const countryCode = userStore.getSelectedCountry || 'US';
      queryParams.push(`country-code=${countryCode}`);

      const config = useRuntimeConfig();
      const baseUrl = `${config.public.baseUrl}/wp-json/content-search/v1/search`
      const queryUrl = `${baseUrl}?${queryParams.join('&')}`;

      if (this.currentAbortControllers[queryUrl]) {
        // cancel any pending requests with the same url
        this.currentAbortControllers[queryUrl].abort();
      }

      this.currentAbortControllers[queryUrl] = new AbortController();
      const signal = this.currentAbortControllers[queryUrl]?.signal;

      try {
        const results = await fetch(queryUrl, { signal });

        if (signal.aborted) {
          return;
        }

        const data = await results.json();

        if (data?.aggregations?.tag_filters && data.aggregations.tag_filters.length > 0) {
          this.data.tags = data.aggregations.tag_filters;
        }

        // filter full category list through aggregations to hide empty categories
        const categoryTree = this.data.categoryTree
        if ((data?.aggregations?.category_filters && data.aggregations.category_filters.length > 0) || this.filters.categories.length > 0) {
          const parentIds = []
          let currentParentId = 0
          // parent must not be hidden if child has match
          // get parent id for any filtered categories
          function getParentIds(categoryId) {
            let categoryData = categoryTree.find((category) => category.term_id === categoryId)
            currentParentId = categoryData.parent

            while (currentParentId != 0) {
              parentIds.push(currentParentId)
              getParentIds(currentParentId)
            }
          }

          this.filters.categories.forEach((activeCategory) => {
            const activeCategoryData = categoryTree.find((category) => category.slug === activeCategory)
            getParentIds(activeCategoryData.term_id)
          })

          this.data.categories = categoryTree.filter((category) => {
            // only show categories that are relevant OR already active OR parent of active
            return (data?.aggregations?.category_filters.find((aggregatedCategory) => aggregatedCategory.key === category.slug))
              || this.filters.categories.find((activeCategory) => activeCategory === category.slug)
              || parentIds.find((parentId) => category.term_id == parentId)
          })
        } else {
          // When no aggregations, show all categories from categoryTree
          this.data.categories = categoryTree
        }

        this.data.results = data.results ?? [];
        this.meta.totalResults = data.totalResults ?? 0;
        this.meta.searchStatus = 2;
        if (this.meta.userOrderBy && this.meta.userOrderBy !== data.sort) {
          this.meta.userOrderBy = data.sort;
        }
      } catch (e) {

        if (e.name === 'AbortError') {
          // Search request was cancelled
          return;
        }

        this.data.results = [];
        this.meta.totalResults = 0;
        this.data.searchError = e;
        this.meta.searchStatus = -1;
      } finally {
        delete this.currentAbortControllers[queryUrl]
      }
    },
  },
})